/**
 * Contact Form Module
 * DSS - DeSight Shopify Theme
 * Handles form validation, floating contact button popup, and map behaviors
 */

import { $, $$ } from '../utils/helpers.js';


class CustomSelect {
  constructor(select) {
    this.select = select;
    if (this.select.dataset.dssCustomSelect === 'true') {
      return;
    }

    this.select.dataset.dssCustomSelect = 'true';
    this.options = Array.from(this.select.options);
    this.isOpen = false;
    this.selectedIndex = this.select.selectedIndex;

    this.build();
    this.bindEvents();
  }

  build() {
    this.select.style.display = 'none';

    this.wrapper = document.createElement('div');
    this.wrapper.className = 'dss-custom-select';
    this.wrapper.setAttribute('role', 'combobox');
    this.wrapper.setAttribute('aria-haspopup', 'listbox');
    this.wrapper.setAttribute('aria-expanded', 'false');

    this.trigger = document.createElement('button');
    this.trigger.className = 'dss-custom-select__trigger';
    this.trigger.type = 'button';
    this.trigger.setAttribute('aria-label', this.select.getAttribute('aria-label') || 'Auswählen');
    this.trigger.innerHTML = `
      <span class="dss-custom-select__value">${this.getDisplayText()}</span>
      <span class="dss-custom-select__arrow" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>
    `;

    this.updatePlaceholderState();

    this.dropdown = document.createElement('ul');
    this.dropdown.className = 'dss-custom-select__dropdown';
    this.dropdown.setAttribute('role', 'listbox');
    this.dropdown.id = `dss-cs-list-${this.select.name || Math.random().toString(36).slice(2, 7)}`;

    this.trigger.setAttribute('aria-controls', this.dropdown.id);

    this.options.forEach((option, index) => {
      const li = document.createElement('li');
      li.className = 'dss-custom-select__option';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', index === this.selectedIndex ? 'true' : 'false');
      li.dataset.index = index;
      li.dataset.value = option.value;
      li.textContent = option.textContent;

      if (option.disabled) li.classList.add('dss-custom-select__option--disabled');
      if (option.selected || index === this.selectedIndex) li.classList.add('dss-custom-select__option--selected');

      this.dropdown.appendChild(li);
    });

    this.wrapper.appendChild(this.trigger);
    this.wrapper.appendChild(this.dropdown);
    this.select.parentNode.insertBefore(this.wrapper, this.select.nextSibling);
  }

  getDisplayText() {
    const selected = this.options[this.selectedIndex];
    if (selected && !selected.disabled) return selected.textContent;
    const firstReal = this.options.find(o => !o.disabled);
    return firstReal ? firstReal.textContent : '';
  }

  updatePlaceholderState() {
    const selected = this.options[this.selectedIndex];
    const isPlaceholder = !selected || selected.disabled || selected.value === '';
    this.wrapper.classList.toggle('dss-custom-select--placeholder', isPlaceholder);
  }

  bindEvents() {
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });

    this.trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!this.isOpen) this.open();
        else this.navigate(e.key === 'ArrowDown' ? 1 : -1);
      }
    });

    this.dropdown.querySelectorAll('.dss-custom-select__option').forEach((li) => {
      li.addEventListener('click', () => {
        if (li.classList.contains('dss-custom-select__option--disabled')) return;
        this.setValue(parseInt(li.dataset.index));
        this.close();
      });

      li.addEventListener('mousedown', (e) => {
        if (li.classList.contains('dss-custom-select__option--disabled')) e.preventDefault();
      });
    });

    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.wrapper.contains(e.target)) {
        this.close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.select.addEventListener('change', () => {
      this.syncFromNative();
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.wrapper.classList.add('active');
    this.wrapper.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.isOpen = false;
    this.wrapper.classList.remove('active');
    this.wrapper.setAttribute('aria-expanded', 'false');
  }

  setValue(index) {
    this.selectedIndex = index;
    this.select.selectedIndex = index;

    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.select.dispatchEvent(new Event('input', { bubbles: true }));

    this.trigger.querySelector('.dss-custom-select__value').textContent = this.options[index].textContent;
    this.updatePlaceholderState();

    this.dropdown.querySelectorAll('.dss-custom-select__option').forEach((li, i) => {
      li.classList.toggle('dss-custom-select__option--selected', i === index);
      li.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  navigate(direction) {
    const items = this.dropdown.querySelectorAll('.dss-custom-select__option:not(.dss-custom-select__option--disabled)');
    const currentIdx = Array.from(items).findIndex(li =>
      parseInt(li.dataset.index) === this.selectedIndex
    );
    const nextIdx = currentIdx + direction;

    if (nextIdx >= 0 && nextIdx < items.length) {
      const target = items[nextIdx];
      this.setValue(parseInt(target.dataset.index));
      target.scrollIntoView({ block: 'nearest' });
    }
  }

  syncFromNative() {
    const idx = this.select.selectedIndex;
    if (idx !== this.selectedIndex) {
      this.selectedIndex = idx;
      this.trigger.querySelector('.dss-custom-select__value').textContent = this.options[idx]?.textContent || '';
      this.dropdown.querySelectorAll('.dss-custom-select__option').forEach((li, i) => {
        li.classList.toggle('dss-custom-select__option--selected', i === idx);
        li.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
    }
  }

  static initAll(container) {
    const root = container || document;
    const selects = root.querySelectorAll('select.field__input, select.dss-custom-select--init');
    selects.forEach(select => {
      if (!select.closest('.dss-custom-select') && !select.dataset.dssCustomSelect) {
        new CustomSelect(select);
      }
    });
  }
}

class ContactForm {
  constructor(container) {
    this.container = container;
    if (!this.container) {
      return;
    }
    this.form = this.container.querySelector('form');
    if (!this.form) {
      return;
    }
    this.init();
  }

  init() {
    this.bindValidation();
    this.bindSubmit();
    this.preselectStore(new URLSearchParams(window.location.search));
    this.handleProbefahrtMode();
  }

  /**
   * Handle Probefahrt mode: show date field, pre-fill subject
   * Triggered by URL param ?type=probefahrt or ?betreff=Probefahrt
   */
  handleProbefahrtMode() {
    const params = new URLSearchParams(window.location.search);
    const isProbefahrt = params.get('type') === 'probefahrt' || 
                         (params.get('betreff') || '').toLowerCase().includes('probefahrt');
    
    if (!isProbefahrt) return;

    // Show Probefahrt date field
    const probefahrtField = this.form.querySelector('[data-dss-probefahrt-field]');
    if (probefahrtField) {
      probefahrtField.classList.add('is-visible');
    }

    // Show and pre-fill subject field
    const subjectField = this.form.querySelector('[data-dss-subject-field]');
    if (subjectField) {
      subjectField.classList.add('is-visible');
      const subjectInput = subjectField.querySelector('input');
      if (subjectInput) {
        subjectInput.value = 'Probefahrt anfragen';
      }
    }
  }

  /**
   * Pre-select the store dropdown based on URL param or cookie
   */
  preselectStore(params) {
    const storeSelect = this.form.querySelector('[data-dss-store-select]');
    if (!storeSelect) return;

    // 1) Check URL param first
    let storeSlug = params.get('store');

    // 2) Fall back to cookie
    if (!storeSlug) {
      const cookieMatch = document.cookie.match(/(?:^|;\s*)dss_store=([^;]*)/);
      storeSlug = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
    }

    if (!storeSlug) return;

    // Match slug against select options (case-insensitive partial match)
    const normalized = storeSlug.toLowerCase().trim();
    const options = Array.from(storeSelect.options);
    const match = options.find(opt => {
      const val = opt.value.toLowerCase();
      // Exact match or slug is contained in the option value
      return val === normalized || val.includes(normalized);
    });

    if (match) {
      storeSelect.value = match.value;
      // Trigger change event for custom select
      storeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  bindValidation() {
    const requiredFields = this.form.querySelectorAll('[required]');
    requiredFields.forEach(field => {
      field.addEventListener('invalid', (e) => this.handleInvalid(e));
      field.addEventListener('input', (e) => this.clearError(e));
    });
  }

  bindSubmit() {
    const submitBtn = this.form.querySelector('button[type="submit"]');
    if (!submitBtn) return;
    submitBtn.addEventListener('click', (e) => {
      const isValid = this.form.checkValidity();
      if (!isValid) return;
      e.preventDefault();
      const btn = e.currentTarget;
      btn.disabled = true;
      btn.innerHTML = 'Wird gesendet...';
      setTimeout(() => {
        this.form.submit();
      }, 200);
    });
  }

  handleInvalid(e) {
    e.preventDefault();
    const field = e.target;
    this.showError(field);
    field.focus();
  }

  showError(field) {
    const wrapper = field.closest('.field');
    if (!wrapper) return;
    wrapper.querySelector('.field__input')?.classList.add('error');
    const existing = wrapper.querySelector('.dss-contact-form__field-error');
    if (!existing) {
      const error = document.createElement('small');
      error.className = 'dss-contact-form__field-error';
      error.textContent = 'Dieses Feld ist erforderlich';
      wrapper.appendChild(error);
    }
  }

  clearError(e) {
    const field = e.target;
    const wrapper = field.closest('.field');
    if (!wrapper) return;
    field.classList.remove('error');
    const error = wrapper.querySelector('.dss-contact-form__field-error');
    if (error) error.remove();
  }
}

class FloatingContactButton {
  constructor() {
    this.btn = $('#dss-floating-contact-btn');
    this.popup = $('#dss-floating-contact-popup');
    this.overlay = $('#dss-floating-contact-overlay');
    this.closeBtn = $('#dss-floating-contact-popup-close');
    this.isProbefahrtMode = false;

    if (!this.btn || !this.popup) {
      return;
    }
    this.init();
  }

  init() {
    this.bindEvents();

    // Nach Formular-Absenden: Success-Message im DOM? → Popup öffnen
    const successMsg = this.popup.querySelector('.dss-contact-form__success');
    if (successMsg) {
      this.open();
      const header = this.popup.querySelector('.dss-floating-contact__popup-header');
      const text = this.popup.querySelector('.dss-floating-contact__popup-text');
      if (header) header.style.display = 'none';
      if (text) text.style.display = 'none';
      // URL nur bereinigen wenn contact_posted=true drin ist
      if (window.location.search.includes('contact_posted=true')) {
        window.history.replaceState({}, '', window.location.pathname + window.location.hash);
      }
    }

    // Auto-open popup when ?type=probefahrt is in the URL (direct link)
    const params = new URLSearchParams(window.location.search);
    if (params.get('type') === 'probefahrt' || (params.get('betreff') || '').toLowerCase().includes('probefahrt')) {
      setTimeout(() => this.open(), 300);
    }
  }

  bindEvents() {
    this.btn.addEventListener('click', () => {
      this.open();
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        this.close();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.close();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('active')) {
        this.close();
      }
    });

    document.addEventListener('shopify:section:load', () => {
      this.reinit();
    });
  }

  open() {
    this.popup.classList.add('active');
    this.btn.setAttribute('aria-expanded', 'true');
    if (this.overlay) this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    this.popup.querySelector('input:not([type="hidden"]):not([type="checkbox"])')?.focus();

    const header = this.popup.querySelector('.dss-floating-contact__popup-header');
    const text = this.popup.querySelector('.dss-floating-contact__popup-text');
    if (header) header.style.display = '';
    if (text) text.style.display = '';

    const resetBtn = this.popup.querySelector('[data-floating-close]');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.close());
    }

    this.addProductField();

    // Manage subject field: show on non-PDP, disable on PDP (product field handles Betreff)
    const form = this.popup.querySelector('form');
    const subjectField = form && form.querySelector('[data-dss-subject-field]');
    const hasProductField = form && form.querySelector('.dss-product-field');

    if (subjectField) {
      if (hasProductField) {
        // PDP: product field has contact[Betreff] → disable subject field to avoid duplicates
        subjectField.style.display = 'none';
        subjectField.querySelectorAll('input').forEach(input => { input.disabled = true; });
      } else {
        // Non-PDP: no product field → show subject field for user to fill in
        subjectField.style.display = 'block';
        subjectField.querySelectorAll('input').forEach(input => { input.disabled = false; });
      }
    }

    this.preselectStore(form, new URLSearchParams(window.location.search));
    this.handleProbefahrtMode();
  }

  /**
   * Handle Probefahrt mode in floating popup
   */
  handleProbefahrtMode() {
    const form = this.popup.querySelector('form');
    if (!form) return;

    const params = new URLSearchParams(window.location.search);
    const isProbefahrt = params.get('type') === 'probefahrt' ||
                         (params.get('betreff') || '').toLowerCase().includes('probefahrt');

    // Track state so re-opens preserve it
    if (isProbefahrt) this.isProbefahrtMode = true;
    const active = isProbefahrt || this.isProbefahrtMode;

    if (!active) return;

    // Show Probefahrt-specific title, hide default title
    const probefahrtTitle = this.popup.querySelector('[data-dss-probefahrt-title]');
    const defaultTitle = this.popup.querySelector('.dss-floating-popup-default-title');
    if (probefahrtTitle) probefahrtTitle.classList.add('is-visible');
    if (defaultTitle) defaultTitle.style.display = 'none';

    // Update aria-label for accessibility
    if (probefahrtTitle) {
      this.popup.setAttribute('aria-label', probefahrtTitle.textContent.trim());
    }

    // Show Probefahrt-specific text, hide default text
    const probefahrtText = this.popup.querySelector('[data-dss-probefahrt-text]');
    const defaultText = this.popup.querySelector('.dss-floating-popup-default-text');
    if (probefahrtText) {
      probefahrtText.classList.add('is-visible');
    }
    if (defaultText) {
      defaultText.style.display = 'none';
    }

    // Show Probefahrt date field
    const probefahrtField = form.querySelector('[data-dss-probefahrt-field]');
    if (probefahrtField) {
      probefahrtField.classList.add('is-visible');
    }

    // Hide the subject field and disable its inputs so it doesn't submit
    const subjectField = form.querySelector('[data-dss-subject-field]');
    if (subjectField) {
      subjectField.style.display = 'none';
      subjectField.querySelectorAll('input').forEach(input => {
        input.disabled = true;
      });
    }

    // Change product field betreff from "Fragen zu..." to "Probefahrt mit Bike: ..."
    const productField = form.querySelector('.dss-product-field input.field__input');
    const productHidden = form.querySelector('.dss-product-field input[type="hidden"]');
    const productTitleEl = document.querySelector('.product__title h1');
    if (productTitleEl) {
      const productName = productTitleEl.textContent.trim();
      const newBetreff = `Probefahrt mit Bike: ${productName}`;
      if (productField) productField.value = newBetreff;
      if (productHidden) productHidden.value = newBetreff;
    }
  }

  /**
   * Pre-select store dropdown in floating popup
   */
  preselectStore(form, params) {
    const storeSelect = form.querySelector('[data-dss-store-select]');
    if (!storeSelect) return;

    let storeSlug = params.get('store');
    if (!storeSlug) {
      const cookieMatch = document.cookie.match(/(?:^|;\s*)dss_store=([^;]*)/);
      storeSlug = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
    }
    if (!storeSlug) return;

    const normalized = storeSlug.toLowerCase().trim();
    const options = Array.from(storeSelect.options);
    const match = options.find(opt => {
      const val = opt.value.toLowerCase();
      return val === normalized || val.includes(normalized);
    });

    if (match) {
      storeSelect.value = match.value;
      storeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  addProductField() {
    const form = this.popup.querySelector('form');
    if (!form) return;

    if (form.querySelector('.dss-product-field')) return;

    const isProductPage = window.location.pathname.includes('/products/');
    if (!isProductPage) return;

    const productTitleEl = document.querySelector('.product__title h1');
    if (!productTitleEl) return;

    const productName = productTitleEl.textContent.trim();
    if (!productName) return;

    let sku = '';
    const variantJsonScript = document.querySelector('[data-selected-variant]');
    if (variantJsonScript) {
      try {
        const variantData = JSON.parse(variantJsonScript.textContent);
        sku = variantData.sku || '';
      } catch (e) {}
    }

    let betreff = `Fragen zu ${productName} stellen`;
    if (sku) {
      betreff += ` (${sku})`;
    }

    const fieldGroup = document.createElement('div');
    fieldGroup.className = 'dss-contact-form__field-group dss-product-field';
    fieldGroup.innerHTML = `
      <div class="dss-contact-form__field field">
        <input
          class="field__input"
          type="text"
          id="FloatingContact-product"
          value="${betreff}"
          disabled
          readonly
          placeholder=" "
        >
        <label class="field__label" for="FloatingContact-product">Betreff</label>
        <input type="hidden" name="contact[Betreff]" value="${betreff}">
      </div>
    `;

    const firstFieldGroup = form.querySelector('.dss-contact-form__field-group');
    if (firstFieldGroup) {
      form.insertBefore(fieldGroup, firstFieldGroup);
    }
  }

  close() {
    this.resetProbefahrtState();
    this.isProbefahrtMode = false;
    this.clearProbefahrtUrl();
    this.popup.classList.remove('active');
    this.btn.setAttribute('aria-expanded', 'false');
    if (this.overlay) this.overlay.classList.remove('active');
    document.body.style.overflow = '';
    this.btn.focus();
  }

  /**
   * Remove Probefahrt URL params so next normal open doesn't trigger Probefahrt mode
   */
  clearProbefahrtUrl() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('type') || params.has('betreff') || params.has('store')) {
      params.delete('type');
      params.delete('betreff');
      params.delete('store');
      const clean = params.toString();
      const newUrl = window.location.pathname + (clean ? '?' + clean : '') + window.location.hash;
      history.replaceState({}, '', newUrl);
    }
  }

  /**
   * Reset Probefahrt state on close so next open defaults to normal
   */
  resetProbefahrtState() {
    const form = this.popup.querySelector('form');
    if (!form) return;

    // Hide probefahrt fields
    const probefahrtField = form.querySelector('[data-dss-probefahrt-field]');
    const subjectField = form.querySelector('[data-dss-subject-field]');
    if (probefahrtField) probefahrtField.classList.remove('is-visible');
    if (subjectField) {
      // On non-PDP pages, keep subject field visible; on PDP pages, hide it
      const hasProductField = form.querySelector('.dss-product-field');
      subjectField.style.display = hasProductField ? 'none' : 'block';
      subjectField.classList.remove('is-visible');
      subjectField.querySelectorAll('input').forEach(input => {
        input.disabled = !!hasProductField;
      });
    }

    // Reset subject input
    const subjectInput = subjectField?.querySelector('input');
    if (subjectInput) subjectInput.value = '';

    // Restore product field betreff to default
    const productField = form.querySelector('.dss-product-field input.field__input');
    const productHidden = form.querySelector('.dss-product-field input[type="hidden"]');
    if (productField && productHidden) {
      const productTitleEl = document.querySelector('.product__title h1');
      if (productTitleEl) {
        const productName = productTitleEl.textContent.trim();
        let betreff = `Fragen zu ${productName} stellen`;
        let sku = '';
        const variantJsonScript = document.querySelector('[data-selected-variant]');
        if (variantJsonScript) {
          try { sku = JSON.parse(variantJsonScript.textContent).sku || ''; } catch (e) {}
        }
        if (sku) betreff += ` (${sku})`;
        productField.value = betreff;
        productHidden.value = betreff;
      }
    }

    // Restore default title, hide probefahrt title
    const probefahrtTitle = this.popup.querySelector('[data-dss-probefahrt-title]');
    const defaultTitle = this.popup.querySelector('.dss-floating-popup-default-title');
    if (probefahrtTitle) probefahrtTitle.classList.remove('is-visible');
    if (defaultTitle) defaultTitle.style.display = '';

    // Restore default aria-label
    if (defaultTitle) {
      this.popup.setAttribute('aria-label', defaultTitle.textContent.trim());
    }

    // Restore default text, hide probefahrt text
    const probefahrtText = this.popup.querySelector('[data-dss-probefahrt-text]');
    const defaultText = this.popup.querySelector('.dss-floating-popup-default-text');
    if (probefahrtText) probefahrtText.classList.remove('is-visible');
    if (defaultText) defaultText.style.display = '';
  }

  reinit() {
    this.btn = $('#dss-floating-contact-btn');
    this.popup = $('#dss-floating-contact-popup');
    this.overlay = $('#dss-floating-contact-overlay');
    this.closeBtn = $('#dss-floating-contact-popup-close');
    if (this.btn && this.popup) this.bindEvents();
  }
}

function initCustomSelects(container) {
  CustomSelect.initAll(container);
}

function initContactForms() {
  const formContainers = $$('.dss-contact-form');
  formContainers.forEach(container => {
    new ContactForm(container);
    initCustomSelects(container);
  });
}

function initFloatingContact() {
  const btnExists = !!$('#dss-floating-contact-btn');
  if (btnExists) {
    new FloatingContactButton();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initContactForms();
    initFloatingContact();
  });
} else {
  initContactForms();
  initFloatingContact();
}


document.addEventListener('shopify:section:load', (event) => {
  const contactForms = event.target?.querySelectorAll?.('.dss-contact-form');
  if (contactForms) {
    contactForms.forEach(container => {
      new ContactForm(container);
      initCustomSelects(container);
    });
  }
  if (event.target) {
    initCustomSelects(event.target);
  }
  if ($('#dss-floating-contact-btn', event.target)) {
    new FloatingContactButton();
  }
});

export { CustomSelect, ContactForm, FloatingContactButton, initContactForms, initFloatingContact, initCustomSelects };
